import { useEffect, useState } from 'react';
import { below } from './breakpoints';

/**
 * True below Carbon's `md` breakpoint (672px) — the width at which a data table
 * stops fitting and the same rows are rendered as tiles instead.
 *
 * Was a hardcoded 640px matching the old `.record-cards` media query; it now
 * tracks Carbon's grid so the DOM switches on exactly the width the CSS does.
 */
const QUERY = below('md');

/**
 * Renders the table/card fallback as mutually exclusive DOM subtrees instead of
 * both-at-once with CSS `display:none` — the CSS-only version duplicates every
 * row's accessible name/role, which is invisible to real users (browsers exclude
 * `display:none` from the accessibility tree) but breaks single-match queries in
 * JSDOM, which doesn't evaluate media queries at all.
 *
 * `window.matchMedia` is polyfilled to `matches: false` in this project's JSDOM
 * environment (vitest.setup.ts), so `isMobile` is always `false` there and tests
 * exercise the table path. A test that needs the card path must stub
 * `window.matchMedia` itself.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && (window.matchMedia?.(QUERY)?.matches ?? false),
  );

  useEffect(() => {
    if (typeof window === 'undefined' || window.matchMedia === undefined) return;
    const mql = window.matchMedia(QUERY);
    // Re-sync on mount: the viewport can change between the initial render and
    // this effect running, and that first `change` event has already been missed.
    setIsMobile(mql.matches);
    const handler = (event: MediaQueryListEvent): void => setIsMobile(event.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isMobile;
}
